/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useId, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Label } from '@atlaskit/form/label';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

import {
	isAllowedFocusAskLinkUrl,
	normalizeFocusAskLinkUrl,
} from '../../is-allowed-focus-ask-link-url';
import { FocusAskLinkRow } from '../../link-row/FocusAskLinkRow';
import messages from '../../messages';
import type { FocusAskLinkData } from '../../types';

const styles = cssMap({
	section: {
		width: '100%',
		minWidth: '0',
	},
	// A single rounded, bordered container holding all links — mirrors the Focus
	// product's SmartLinksList. The between-row dividers are applied per-row by
	// FocusAskLinkRow (`showDivider`), since `cssMap` disallows `& > *` selectors.
	linksList: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		overflow: 'hidden',
	},
	linksEmptyState: {
		alignItems: 'center',
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		justifyContent: 'center',
		minHeight: '8rem',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		textAlign: 'center',
	},
});

type FocusAskLinksSectionProps = {
	links: FocusAskLinkData[];
	onChange: (links: FocusAskLinkData[]) => void;
	isEditorOpen: boolean;
	onEditorOpenChange: (isOpen: boolean) => void;
	canEdit?: boolean;
	isDisabled?: boolean;
};

export const FocusAskLinksSection = ({
	links,
	onChange,
	isEditorOpen,
	onEditorOpenChange,
	canEdit = true,
	isDisabled = false,
}: FocusAskLinksSectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const linkUrlFieldId = useId();
	const linkTextFieldId = useId();
	const [draft, setDraft] = useState({ text: '', url: '' });

	const visibleLinks = links.filter((link) => link.url.trim());
	const shouldShowLinks = visibleLinks.length > 0;
	const shouldRenderLinksSection = shouldShowLinks || canEdit || isEditorOpen;
	const isSaveDisabled = !isAllowedFocusAskLinkUrl(draft.url);

	const handleOpenEditor = useCallback(() => {
		setDraft({ text: '', url: '' });
		onEditorOpenChange(true);
	}, [onEditorOpenChange]);

	const handleCancelEditor = useCallback(() => {
		setDraft({ text: '', url: '' });
		onEditorOpenChange(false);
	}, [onEditorOpenChange]);

	const handleSaveLink = useCallback(() => {
		// Normalize the same way the modal's LinkForm does, so a bare "asd" is
		// stored (and displayed) as "http://asd/". Invalid URLs are rejected.
		const nextUrl = normalizeFocusAskLinkUrl(draft.url);
		const nextText = draft.text.trim();
		if (!nextUrl) {
			return;
		}
		onChange([...links, nextText ? { url: nextUrl, linkText: nextText } : { url: nextUrl }]);
		setDraft({ text: '', url: '' });
		onEditorOpenChange(false);
	}, [draft.text, draft.url, links, onChange, onEditorOpenChange]);

	const handleRemoveLink = useCallback(
		(indexToRemove: number) => {
			onChange(visibleLinks.filter((_, index) => index !== indexToRemove));
		},
		[onChange, visibleLinks],
	);

	if (!shouldRenderLinksSection) {
		return null;
	}

	return (
		<Stack space="space.100" xcss={styles.section}>
			<Inline spread="space-between" alignBlock="center">
				<Text weight="semibold">{formatMessage(messages.linksSectionLabel)}</Text>
				{canEdit && (
					<IconButton
						spacing="compact"
						icon={AddIcon}
						label={formatMessage(messages.addLinkButtonLabel)}
						onClick={handleOpenEditor}
						isDisabled={isDisabled}
					/>
				)}
			</Inline>
			<Stack space="space.100">
				{shouldShowLinks && (
					<Stack space="space.0" xcss={styles.linksList}>
						{visibleLinks.map((link, index) => (
							<FocusAskLinkRow
								key={`${link.url}-${index}`}
								link={link}
								canRemove={canEdit}
								removeLabel={formatMessage(messages.removeLinkButtonLabel)}
								onRemove={() => handleRemoveLink(index)}
								showDivider={index < visibleLinks.length - 1}
							/>
						))}
					</Stack>
				)}
				{isEditorOpen && (
					<Stack space="space.100">
						<Stack space="space.050">
							<Label htmlFor={linkUrlFieldId}>
								{formatMessage(messages.linkUrlFieldLabel)}
								<RequiredAsterisk />
							</Label>
							<Textfield
								id={linkUrlFieldId}
								value={draft.url}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
									setDraft((prev) => ({ ...prev, url: event.target.value }))
								}
								placeholder={formatMessage(messages.linkUrlFieldPlaceholder)}
								isCompact
							/>
						</Stack>
						<Stack space="space.050">
							<Label htmlFor={linkTextFieldId}>{formatMessage(messages.linkTextFieldLabel)}</Label>
							<Textfield
								id={linkTextFieldId}
								value={draft.text}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
									setDraft((prev) => ({ ...prev, text: event.target.value }))
								}
								placeholder={formatMessage(messages.linkTextFieldPlaceholder)}
								isCompact
							/>
						</Stack>
						<Inline space="space.100" alignBlock="center" spread="space-between">
							<Button appearance="subtle" onClick={handleCancelEditor}>
								{formatMessage(messages.cancelButton)}
							</Button>
							<Button
								appearance="primary"
								onClick={handleSaveLink}
								isDisabled={isDisabled || isSaveDisabled}
							>
								{formatMessage(messages.saveLinkButtonLabel)}
							</Button>
						</Inline>
					</Stack>
				)}
				{!shouldShowLinks && !isEditorOpen && (
					<Box xcss={styles.linksEmptyState}>
						<Stack space="space.100" alignInline="center">
							<Text>{formatMessage(messages.linksEmptyStateDescription)}</Text>
						</Stack>
						{canEdit && (
							<Button appearance="primary" onClick={handleOpenEditor} isDisabled={isDisabled}>
								{formatMessage(messages.addLinkButtonLabel)}
							</Button>
						)}
					</Box>
				)}
			</Stack>
		</Stack>
	);
};
